import { ExclamationTriangleIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { versionSummary } from "../components/LoyaltyProgramHistory";
import Toast from "../components/Toast";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { formatDateTime } from "../services/datetime";
import { copyProgram, fetchPrograms, setProgramActive } from "../services/loyaltyService";
import { can } from "../services/permissions";
import { labelFor } from "../services/enumLabels";

/**
 * Loyalty → Programs: one programme per branch.
 *
 * Each branch runs at most one programme, which decides how a visit there
 * earns; the customer's balance is one wallet across the business. A branch
 * without a programme earns nothing — listed at the top as a warning. Managers
 * see only their branches (the server scopes the list).
 */
export default function LoyaltyPrograms() {
  const { t, i18n } = useTranslation();
  const mayManage = can(getCurrentUser(), "loyalty.manage");
  const [data, setData] = useState({ programs: [], branches: [], currency: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [copying, setCopying] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    fetchPrograms()
      .then(setData)
      .catch((err) => setError(apiErrorMessage(err, t("loyalty.loadFailed"))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (program) => {
    setBusyId(program.id);
    try {
      await setProgramActive(program.id, !program.is_active);
      setToast({
        type: "success",
        message: program.is_active ? t("loyalty.programs.deactivated") : t("loyalty.programs.activated"),
      });
      load();
    } catch (err) {
      setToast({ type: "error", message: apiErrorMessage(err, t("loyalty.actionFailed")) });
    } finally {
      setBusyId(null);
    }
  };

  const without = data.branches.filter((branch) => !branch.program_id);
  const when = (iso) => formatDateTime(iso, i18n.language);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("loyalty.programs.title")}</h1>
          <p className="text-sm text-ink-2">{t("loyalty.programs.subtitle")}</p>
        </div>
        {mayManage && (
          <Link to="/loyalty/programs/new" className="btn-primary inline-flex items-center gap-1">
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            {t("loyalty.programs.new")}
          </Link>
        )}
      </div>

      {error && (
        <div
          className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
          role="alert"
        >
          {error}
        </div>
      )}

      {!loading && without.length > 0 && (
        <ul className="space-y-1 rounded-lg border border-warning-line bg-warning-wash px-3 py-2 text-sm text-warning-text">
          {without.map((branch) => (
            <li key={branch.id} className="flex flex-wrap items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="font-semibold">{branch.name}</span>
              <span>— {t("loyalty.programs.noProgram")}</span>
              {mayManage && (
                <Link
                  to={`/loyalty/programs/new?branch=${branch.id}`}
                  className="ms-auto font-medium underline"
                >
                  {t("loyalty.programs.createHere")}
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}

      {loading ? (
        <p className="py-16 text-center text-sm text-ink-2">{t("common.loading")}</p>
      ) : data.programs.length === 0 ? (
        <p className="card p-6 text-center text-sm text-ink-2">{t("loyalty.programs.empty")}</p>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-start text-sm">
            <thead className="border-b border-line text-xs uppercase tracking-wide text-ink-2">
              <tr>
                <th className="px-4 py-2 text-start">{t("loyalty.programs.branch")}</th>
                <th className="px-4 py-2 text-start">{t("loyalty.programs.status")}</th>
                <th className="px-4 py-2 text-start">{t("loyalty.programs.rules")}</th>
                <th className="px-4 py-2 text-start">{t("loyalty.programs.updated")}</th>
                <th className="px-4 py-2 text-start">{t("loyalty.programs.version")}</th>
                <th className="px-4 py-2 text-end">{t("loyalty.programs.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {data.programs.map((program) => (
                <tr key={program.id} className="border-b border-line align-top last:border-0">
                  <td className="px-4 py-3 font-semibold">
                    <Link to={`/loyalty/programs/${program.id}`} className="hover:underline">
                      {program.branch?.name ?? "—"}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        program.is_active ? "bg-accent-wash text-accent-text" : "bg-raised text-ink-2"
                      }`}
                    >
                      {program.is_active ? t("loyalty.programs.on") : t("loyalty.programs.off")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink">
                    {labelFor("loyalty_mode", program.mode)}
                    <span className="block text-xs text-ink-2">
                      {versionSummary(program, data.currency, t)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-ink-2">
                    <span dir="ltr">
                      {when(program.current_version?.effective_from ?? program.updated_at)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-2">
                    {program.current_version ? `v${program.current_version.version_number}` : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-x-3 gap-y-1 text-sm font-medium text-accent-text">
                      <Link to={`/loyalty/programs/${program.id}`}>{t("loyalty.programs.view")}</Link>
                      {mayManage && (
                        <Link to={`/loyalty/programs/${program.id}/edit`}>{t("common.edit")}</Link>
                      )}
                      {mayManage && (
                        <Link to={`/loyalty/programs/new?copy=${program.id}`}>
                          {t("loyalty.programs.copy")}
                        </Link>
                      )}
                      {mayManage && without.length > 0 && (
                        <button type="button" onClick={() => setCopying(program)}>
                          {t("loyalty.programs.copyToBranches")}
                        </button>
                      )}
                      <Link to={`/loyalty/programs/${program.id}?tab=history`}>
                        {t("loyalty.history.tabs.history")}
                      </Link>
                      {mayManage && (
                        <button
                          type="button"
                          disabled={busyId === program.id}
                          onClick={() => toggle(program)}
                        >
                          {program.is_active
                            ? t("loyalty.programs.deactivate")
                            : t("loyalty.programs.activate")}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {copying && (
        <CopyToBranches
          program={copying}
          branches={without}
          onClose={() => setCopying(null)}
          onDone={(message) => {
            setCopying(null);
            setToast({ type: "success", message });
            load();
          }}
        />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

/** "Copy to branches…": one new programme (version 1) per ticked branch, all with the source's settings. */
function CopyToBranches({ program, branches, onClose, onDone }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const flip = (id) =>
    setSelected((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { message } = await copyProgram(program.id, { branch_ids: selected });
      onDone(message || t("loyalty.programs.copiedCount", { count: selected.length }));
    } catch (err) {
      const fields = apiFieldErrors(err);
      setError(Object.values(fields)[0]?.[0] ?? apiErrorMessage(err, t("loyalty.actionFailed")));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <div role="dialog" aria-modal="true" className="card w-full max-w-md space-y-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">{t("loyalty.programs.copyToBranchesTitle")}</h2>
            <p className="text-sm text-ink-2">
              {t("loyalty.programs.copyToBranchesHint", { branch: program.branch?.name ?? "" })}
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary px-2"
            aria-label={t("common.close")}
            onClick={onClose}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {branches.map((branch) => (
            <li key={branch.id}>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-raised">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--pl-accent)]"
                  checked={selected.includes(branch.id)}
                  onChange={() => flip(branch.id)}
                />
                <span>{branch.name}</span>
              </label>
            </li>
          ))}
        </ul>

        {error && <p className="field-error">{error}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || selected.length === 0}
            onClick={submit}
          >
            {busy ? t("common.saving") : t("loyalty.programs.copyCount", { count: selected.length })}
          </button>
        </div>
      </div>
    </div>
  );
}
